import {
  SUPPLIERS_QUERY,
  UPDATE_SUPPLIER_MUTATION,
} from "@/apis/gql/apolloClient";
import {
  type ApolloError,
  type FetchResult,
  useMutation,
} from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type Update = {
  supplierId: string;
  updatedFields: {
    accountId: string | null;
  };
};

export const useUpdateSupplier = (
  {
    config,
  }: {
    config?: {
      toasts?: {
        loading?: boolean;
      };
    };
  } = {
    config: {
      toasts: {
        loading: true,
      },
    },
  },
): [
  (
    data: Update,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<FetchResult>,
  {
    data: Update;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const [updateSupplier, { data, loading, error }] = useMutation(
    UPDATE_SUPPLIER_MUTATION,
    { refetchQueries: [SUPPLIERS_QUERY] },
  );

  const handleUpdate = async (
    data: Update,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    toast.dismiss();

    const toastId =
      config?.toasts?.loading && toast.loading(t("generic_loading"));

    try {
      const response = await updateSupplier({
        variables: data,
      });
      if (response.errors) {
        throw new Error("error creating line item");
      }
      onSuccess();
      toastId && toast.dismiss(toastId);
      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId || undefined,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleUpdate, { data, loading, error }];
};
